import React from 'react';
import loadable from '@loadable/component';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

// Real, committed Privacy Policy content -- rendered whenever there's no
// (or an unreachable) Sharetribe Console-hosted asset for this page, which
// in practice has always been the case in this environment, so this is
// effectively the live page content, not just an error fallback.
const fallbackPrivacyPolicy = `
*Last updated: August 2026*

*This policy was drafted to accurately describe Coachify as built. It is not a substitute for legal advice -- have an attorney review it, especially as the platform and the volume of payments it processes grow.*

Coachify ("Coachify," "we," "us") operates two connected products: **Coachify Marketplace** (coachifyusa.com), where coaches and businesses ("Providers") offer paid sessions and Customers book and pay for them, and **Coachify Maps** (discovery.coachifyusa.com and the Coachify mobile app), a free discovery layer for finding Providers nearby. This policy covers both. Coachify Maps has its own more detailed privacy policy at discovery.coachifyusa.com/privacy for anything specific to that product; where the two differ, the more specific one governs.

## 1. Information we collect

**Account information.** Name, email address, phone number, and password (stored as a salted hash, never in plain text) when you create an account.

**Profile information.** Whatever a Provider chooses to add to a public profile -- bio, photos, credentials, categories/specialties, availability, pricing, and a business location (Providers are instructed to use a business address, studio, or general area -- never a home address).

**Booking and payment information.** Session details, booking history, messages exchanged through the platform, and payment information. Card details are collected and stored by our payment processor, Stripe, directly -- Coachify does not store your full card number. We do retain records of transaction amounts, dates, and the parties involved, as required for accounting, tax, and dispute-resolution purposes.

**Location information.** If you use the map to find nearby Providers, your device's location is used to center the map and is not stored on our servers beyond that session. A Provider's published pin is self-reported, one-time information they choose to share, not continuous tracking.

**Communications.** Messages you send through Coachify's messaging features, and any support requests you submit to us.

**Usage and device information.** Standard technical data (IP address, browser type, device identifiers, pages visited) collected automatically for security, fraud prevention, and product improvement.

## 2. How we use your information

- To operate the marketplace: creating and displaying profiles, processing bookings, facilitating payments, and enabling communication between Customers and Providers.
- To process payments and payouts through Stripe, including calculating and collecting Coachify's platform booking fee.
- To send you booking confirmations, reminders, and service-related notifications.
- To investigate and prevent fraud, abuse, and violations of our Terms of Service, including reviewing content reported through our in-app reporting tools.
- To comply with legal obligations, including tax reporting for Provider payouts.
- To improve and maintain the platform.

We do not sell your personal information.

## 3. How your information is shared

**With the other party to a booking.** Booking a session necessarily means sharing information with the Provider or Customer on the other side -- your name, contact details, and any information you choose to include in messages or booking notes. **Once you share information directly with another user, what they do with it is between you and them** -- Coachify is not responsible for a user's independent use, storage, or disclosure of information you voluntarily gave them, though misuse on our platform (harassment, spam, etc.) can and should be reported to us and may result in account action.

**With service providers.** Stripe (payments), hosting and infrastructure providers, and email/communications providers, each bound by their own data-protection obligations to us.

**With Coachify Maps.** If you link your Marketplace account to a Coachify Maps profile, limited account information (name, category, verification status, avatar) is shared between the two products so your identity stays consistent across both.

**For legal reasons.** If required by law, subpoena, or to protect the rights, property, or safety of Coachify, our users, or the public.

We do not share your information with third parties for their own marketing purposes.

## 4. Your choices

- You can edit or remove information from your public profile at any time.
- You can delete your account, which removes your profile and personal account data, subject to records we're legally required to retain (e.g., transaction and tax records).
- You can contact support@coachifyusa.com with any privacy question or request.

## 5. Age requirements

You must be at least 18 years old to create an account, book a session, or offer coaching services on Coachify Marketplace. By creating an account, you represent that you meet this requirement and that you are legally permitted to enter into the agreements described in our Terms of Service in your jurisdiction. (Coachify Maps separately supports guardian-managed profiles for minors in a discovery-only, non-transactional context -- see its own privacy policy for details. That feature does not extend to booking or providing paid services on the Marketplace.)

## 6. Data retention and security

We retain account and transaction data for as long as your account is active and as needed to comply with legal, tax, and accounting obligations after account closure. We use industry-standard technical and organizational measures to protect your data, but no system is completely secure, and we cannot guarantee absolute security.

## 7. Changes to this policy

We may update this policy from time to time. Material changes will be posted here with an updated date.

## 8. Contact

Questions about this policy: support@coachifyusa.com, or visit coachifyusa.com/support.
`;

// Create fallback content (array of sections) in page asset format:
export const fallbackSections = {
  sections: [
    {
      sectionType: 'article',
      sectionId: 'privacy',
      appearance: { fieldType: 'customAppearance', backgroundColor: '#ffffff' },
      title: { fieldType: 'heading1', content: 'Privacy Policy' },
      blocks: [
        {
          blockType: 'defaultBlock',
          blockId: 'hero-content',
          text: {
            fieldType: 'markdown',
            content: fallbackPrivacyPolicy,
          },
        },
      ],
    },
  ],
  meta: {
    pageTitle: {
      fieldType: 'metaTitle',
      content: 'Privacy policy page',
    },
    pageDescription: {
      fieldType: 'metaDescription',
      content: 'Privacy policy fetch failed',
    },
  },
};

// This is the fallback page, in case there's no Privacy Policy asset defined in Console.
const FallbackPage = props => {
  return <PageBuilder pageAssetsData={fallbackSections} {...props} />;
};

export default FallbackPage;
